(() => {
  'use strict';
  if (window.TMFavorites) return;
  const configNode = document.getElementById('TMFavoritesConfig');
  const dialog = document.getElementById('TMFavoritesDialog');
  if (!configNode || !dialog || typeof dialog.showModal !== 'function') return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  const labels = config.labels;
  const key = 'tmedals:favorites:v1';
  const cartKey = 'tmedals:cart:updated';
  const root = (config.root || '/').replace(/\/?$/, '/');
  const cartURL = config.cart_url || `${root}cart`;
  const grid = dialog.querySelector('[data-tm-favorites-grid]');
  const status = dialog.querySelector('[data-tm-favorites-status]');
  const toast = document.querySelector('[data-tm-favorites-toast]');
  const cache = new Map();
  let items = new Map();
  let persistent = true;
  let sort = 'recent';
  let opener;
  let toastTimer;
  let run = 0;
  let cart = null;
  let cartLoading = false;
  let cartRun = 0;
  let adding = '';
  const uncertain = new Set();
  const errors = new Map();
  const recentAdds = new Set();
  const hover = matchMedia('(hover: hover) and (pointer: fine)');
  const resumeKey = 'tmedals:favorites:share-login';
  const sharePrefix = '#favorites=v1.';
  let shared = null;
  let removedItems = [];
  let clearSelection = [];
  let panelTrigger;

  function activeItems() { return shared || items; }

  function setPanel(name, trigger) {
    if (trigger) panelTrigger = trigger;
    dialog.querySelectorAll('[data-tm-panel]').forEach(panel => { panel.hidden = panel.dataset.tmPanel !== name; });
    dialog.querySelector('[data-tm-favorites-share]').setAttribute('aria-expanded', String(name === 'share' || name === 'login'));
    dialog.querySelector('[data-tm-favorites-share]').setAttribute('aria-controls', name === 'login' ? 'TMFavoritesLoginPanel' : 'TMFavoritesSharePanel');
    dialog.querySelector('[data-tm-favorites-clear]').setAttribute('aria-expanded', String(name === 'clear'));
    if (name) {
      const panel = dialog.querySelector(`[data-tm-panel="${name}"]`);
      (name === 'clear' ? panel.querySelector('[data-tm-panel-close]') : panel.querySelector('h3')).focus({ preventScroll: true });
      panel.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    }
  }

  function updateTools() {
    const isShared = shared !== null;
    dialog.querySelector('[data-tm-favorites-heading]').textContent = isShared ? labels.shared_title : labels.title;
    dialog.querySelector('[data-tm-favorites-total]').textContent = activeItems().size;
    dialog.querySelector('[data-tm-favorites-note]').textContent = isShared ? labels.shared_note : persistent ? labels.browser_note : labels.temporary_note;
    dialog.querySelector('[data-tm-favorites-clear]').hidden = isShared;
    dialog.querySelector('[data-tm-favorites-clear]').disabled = !items.size;
    dialog.querySelector('[data-tm-favorites-share]').hidden = isShared;
    dialog.querySelector('[data-tm-favorites-share]').disabled = !items.size;
    dialog.querySelector('[data-tm-favorites-own]').hidden = !isShared;
    const saveAll = dialog.querySelector('[data-tm-favorites-save-all]');
    saveAll.hidden = !isShared;
    saveAll.disabled = !shared?.size || Array.from(shared.keys()).every(id => items.has(id));
    dialog.querySelector('[data-tm-favorites-undo-bar]').hidden = isShared || !removedItems.length;
    dialog.querySelector('[data-tm-favorites-empty-title]').textContent = isShared ? labels.shared_empty : labels.empty_title;
    dialog.querySelector('[data-tm-favorites-empty-text]').textContent = isShared ? labels.shared_empty_text : labels.empty_text;
  }

  function removeItems(ids, message) {
    if (persistent) items = read();
    removedItems = ids.map(id => items.get(id)).filter(Boolean);
    removedItems.forEach(item => items.delete(item.id));
    setPanel('');
    changed();
    dialog.querySelector('[data-tm-favorites-undo-message]').textContent = message;
    status.textContent = '';
    if (removedItems.length) dialog.querySelector('[data-tm-favorites-undo]').focus({ preventScroll: true });
  }

  function restoreItems() {
    if (persistent) items = read();
    const missing = removedItems.filter(item => !items.has(item.id));
    if (items.size + missing.length > 500) { announce(labels.limit); return; }
    missing.forEach(item => items.set(item.id, item));
    removedItems = [];
    changed();
    refresh();
    announce(labels.restored);
    (grid.querySelector('[data-tm-favorite-remove]') || dialog.querySelector('[data-tm-favorites-close]')).focus({ preventScroll: true });
  }

  function makeShareLink() {
    const selection = Array.from(items.values());
    if (!selection.length) return '';
    if (selection.length > 50) { announce(labels.share_limit); return ''; }
    const bytes = new TextEncoder().encode(JSON.stringify(selection.map(item => [item.id, item.handle])));
    const encoded = btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join('')).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    const url = new URL(root, location.origin);
    url.hash = sharePrefix + encoded;
    if (url.href.length > 12000) { announce(labels.share_too_long); return ''; }
    return url.href;
  }

  function showShare(trigger) {
    if (persistent) items = read();
    sync();
    if (!items.size) return;
    if (config.require_login_to_share && !config.logged_in) { setPanel('login', trigger); return; }
    const url = makeShareLink();
    if (!url) return;
    dialog.querySelector('[data-tm-favorites-share-link]').value = url;
    dialog.querySelector('[data-tm-favorites-native]').hidden = typeof navigator.share !== 'function';
    setPanel('share', trigger);
    announce(labels.share_ready);
  }

  async function copyShareLink() {
    const input = dialog.querySelector('[data-tm-favorites-share-link]');
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(input.value);
      announce(labels.copied);
    } catch (_) {
      input.focus();
      input.select();
      input.setSelectionRange(0, input.value.length);
      announce(labels.copy_manual);
    }
  }

  function decodeShareLink() {
    if (!location.hash.startsWith('#favorites=')) return null;
    const hash = location.hash;
    if (!hash.startsWith(sharePrefix) || hash.length > 12000) throw new Error('Invalid share link');
    const encoded = hash.slice(sharePrefix.length);
    if (!/^[A-Za-z0-9_-]+$/.test(encoded)) throw new Error('Invalid share link');
    const raw = atob(encoded.replace(/-/g, '+').replace(/_/g, '/'));
    const selection = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(raw, character => character.charCodeAt(0))));
    if (!Array.isArray(selection) || !selection.length || selection.length > 50) throw new Error('Invalid share link');
    const result = new Map();
    selection.forEach((value, index) => {
      if (!Array.isArray(value) || value.length !== 2 || typeof value[0] !== 'string' || value[0].length > 20 || typeof value[1] !== 'string' || value[1].length > 255) throw new Error('Invalid share item');
      const item = clean({ id: value[0], handle: value[1], title: value[1].replace(/-/g, ' '), savedAt: selection.length - index });
      if (!item || result.has(item.id)) throw new Error('Invalid share item');
      result.set(item.id, item);
    });
    return result;
  }

  function saveShared(ids) {
    if (!shared) return;
    if (persistent) items = read();
    const selection = ids.map(id => shared.get(id)).filter(item => item && !items.has(item.id));
    if (items.size + selection.length > 500) { announce(labels.limit); return; }
    selection.forEach(item => {
      const data = cache.get(item.id);
      items.set(item.id, { ...item, title: data?.title || item.title, image: imageURL(data?.featured_image || item.image), savedAt: Date.now() });
    });
    changed();
    announce(persistent ? labels.saved_all : labels.temporary_note);
  }

  function openList(trigger, sharedList = null) {
    if (persistent) items = read();
    shared = sharedList;
    if (!shared && location.hash.startsWith('#favorites=')) history.replaceState(history.state, '', location.pathname + location.search);
    setPanel('');
    sync();
    opener = trigger || document.querySelector('[data-tm-favorites-open]');
    cache.clear();
    cart = null;
    cartLoading = true;
    status.textContent = '';
    render();
    dialog.querySelector('.tm-favorites-body').scrollTop = 0;
    toast.hidden = true;
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('tm-favorites-open');
    dialog.querySelector('[data-tm-favorites-close]').focus();
    refresh();
    readCart();
  }

  function openSharedLink() {
    if (!location.hash.startsWith('#favorites=')) return false;
    try { openList(null, decodeShareLink()); }
    catch (_) { openList(null, new Map()); announce(labels.shared_invalid); }
    return true;
  }

  function productURL(item) { return `${root}products/${encodeURIComponent(item.handle)}`; }

  function singleVariant(data) {
    if (!data || data.failed || data.missing || data.requires_selling_plan || data.gift_card || !Array.isArray(data.variants) || data.variants.length !== 1) return null;
    const variant = data.variants[0];
    if (!/^\d+$/.test(String(variant.id))) return null;
    const rule = variant.quantity_rule;
    if (rule && (rule.min > 1 || rule.increment > 1 || rule.max === 0)) return null;
    return variant;
  }

  function quantityInCart(id) {
    return cart?.items.reduce((total, item) => total + (String(item.variant_id || item.id) === String(id) ? item.quantity : 0), 0) || 0;
  }

  function applyCart(value) {
    cart = value;
    document.querySelectorAll('.tm-cart-link').forEach(link => {
      let count = link.querySelector('strong');
      if (!count) { count = node('strong'); link.append(count); }
      count.textContent = value.item_count;
      count.hidden = value.item_count === 0;
      count.setAttribute('aria-hidden', 'true');
      link.setAttribute('aria-label', `${labels.cart} (${value.item_count})`);
    });
    dialog.querySelectorAll('[data-tm-favorites-cart-count]').forEach(count => {
      count.textContent = value.item_count;
      count.hidden = value.item_count === 0;
    });
    dialog.querySelector('[data-tm-favorites-cart]').setAttribute('aria-label', `${labels.view_cart} (${value.item_count})`);
    if (value.currency) config.currency = value.currency;
    for (const id of uncertain) {
      const variant = singleVariant(cache.get(id));
      if (variant && quantityInCart(variant.id)) uncertain.delete(id);
    }
    for (const id of errors.keys()) {
      const variant = singleVariant(cache.get(id));
      if (variant && quantityInCart(variant.id)) errors.delete(id);
    }
  }

  async function readCart() {
    const current = ++cartRun;
    cartLoading = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart unavailable');
      const value = await response.json();
      if (!Array.isArray(value.items) || !Number.isFinite(value.item_count) || value.items.some(item => !Number.isFinite(item.quantity))) throw new Error('Invalid cart');
      if (current !== cartRun) return null;
      applyCart(value);
      return value;
    } catch (_) {
      if (current === cartRun) cart = null;
      return null;
    } finally {
      clearTimeout(timeout);
      if (current === cartRun) { cartLoading = false; updateActions(); }
    }
  }

  function sectionTargets() {
    return ['main-cart-items', 'main-cart-footer', 'cart-icon-bubble'].map(id => {
      const element = document.getElementById(id);
      return element ? { id: element.dataset.id || id, element, selector: id === 'cart-icon-bubble' ? null : '.js-contents' } : null;
    }).filter(Boolean).concat(document.getElementById('CartDrawer') ? [{ id: 'cart-drawer', element: document.getElementById('CartDrawer'), selector: null }] : []);
  }

  async function updateSections(sections, targets) {
    if (!targets.length) return;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      if (!sections || targets.some(target => typeof sections[target.id] !== 'string')) {
        const url = new URL(location.href);
        url.searchParams.set('sections', targets.map(target => target.id).join(','));
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        sections = await response.json();
      }
      targets.forEach(target => {
        if (typeof sections[target.id] !== 'string' || !target.element.isConnected) return;
        const html = new DOMParser().parseFromString(sections[target.id], 'text/html');
        const selector = target.selector || (target.id === 'cart-drawer' ? '#CartDrawer' : '.shopify-section');
        const source = html.querySelector(selector);
        const destination = target.selector ? target.element.querySelector(target.selector) : target.element;
        if (source && destination) destination.innerHTML = source.innerHTML;
      });
      if (cart) document.querySelectorAll('cart-items, cart-drawer, #main-cart-footer').forEach(element => element.classList.toggle('is-empty', cart.item_count === 0));
      const drawer = document.querySelector('cart-drawer');
      drawer?.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
    } catch (_) {} finally { clearTimeout(timeout); }
  }

  async function addToCart(id) {
    if (adding || !activeItems().has(id)) return;
    const data = cache.get(id);
    const variant = singleVariant(data);
    if (!variant || !data.available || !variant.available || uncertain.has(id) || !cart) return;
    if (quantityInCart(variant.id)) { updateActions(); return; }
    adding = id;
    errors.delete(id);
    updateActions();
    status.textContent = labels.adding_cart;
    const targets = sectionTargets();
    const payload = { items: [{ id: variant.id, quantity: 1 }] };
    if (targets.length) { payload.sections = targets.map(target => target.id); payload.sections_url = location.pathname; }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let confirmed = false;
    let rejected = false;
    try {
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', credentials: 'same-origin', signal: controller.signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
      const result = await response.json();
      if (!response.ok || result.status) {
        rejected = response.status >= 400 && response.status < 500;
        errors.set(id, typeof result.description === 'string' ? result.description : labels.add_error);
        if (!rejected) throw new Error('Unconfirmed cart update');
        await readCart();
        announce(errors.get(id));
        return;
      }
      const line = result.items?.find(item => String(item.variant_id || item.id) === String(variant.id));
      if (!line || !Number.isFinite(line.quantity)) throw new Error('Unconfirmed cart update');
      confirmed = true;
      recentAdds.add(id);
      if (!await readCart()) {
        uncertain.add(id);
        errors.set(id, labels.cart_refresh_error);
      }
      await updateSections(result.sections, targets);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tm-favorites', productVariantId: String(variant.id), cartData: cart || result })).catch(() => {});
      }
      try { localStorage.setItem(cartKey, String(Date.now())); } catch (_) {}
      announce(cart ? labels.cart_added : labels.cart_refresh_error);
    } catch (_) {
      if (!rejected) uncertain.add(id);
      const latest = await readCart();
      if (latest && quantityInCart(variant.id)) {
        uncertain.delete(id);
        recentAdds.add(id);
        announce(labels.cart_added);
      } else {
        errors.set(id, confirmed ? labels.cart_refresh_error : labels.cart_uncertain);
        announce(errors.get(id));
      }
    } finally {
      clearTimeout(timeout);
      adding = '';
      updateActions();
    }
  }

  function action(item) {
    const data = cache.get(item.id);
    const variant = singleVariant(data);
    let text = labels.checking;
    let href;
    let disabled = true;
    let complete = false;
    if (data?.missing) text = labels.unavailable;
    else if (data?.failed) { text = labels.view_details; href = productURL(item); }
    else if (data) {
      if (!data.available || (variant && !variant.available)) text = labels.sold_out;
      else if (!variant) { text = labels.choose_options; href = productURL(item); }
      else if (adding === item.id) text = labels.adding_cart;
      else if (quantityInCart(variant.id)) {
        text = recentAdds.has(item.id) ? labels.cart_added_button : labels.in_cart;
        complete = true;
        href = cartURL;
      } else if (uncertain.has(item.id) || (!cartLoading && !cart)) { text = labels.check_cart; href = cartURL; }
      else if (cart) { text = labels.add_to_cart; disabled = Boolean(adding); }
    }
    const control = node(href ? 'a' : 'button', `tm-favorites-view${complete ? ' is-added' : ''}`, text);
    if (href) control.href = href;
    else {
      control.type = 'button';
      control.disabled = disabled;
      if (variant) control.dataset.tmFavoriteCartAdd = item.id;
      if (adding === item.id) control.setAttribute('aria-busy', 'true');
    }
    control.setAttribute('aria-label', `${text}: ${data?.title || item.title}`);
    return control;
  }

  function updateActions() {
    grid.querySelectorAll('[data-tm-favorite-item]').forEach(element => {
      const item = activeItems().get(element.dataset.tmFavoriteItem);
      if (!item) return;
      const holder = element.querySelector('[data-tm-favorite-action]');
      const focused = holder.contains(document.activeElement);
      holder.replaceChildren(action(item));
      const error = element.querySelector('[data-tm-favorite-error]');
      error.textContent = errors.get(item.id) || '';
      error.hidden = !error.textContent;
      if (focused && dialog.open) holder.firstElementChild.focus({ preventScroll: true });
    });
  }

  function imageURL(value) {
    if (!value) return '';
    try {
      const url = new URL(value, location.origin);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
    } catch (_) { return ''; }
  }

  function sizedImage(value, width) {
    const src = imageURL(value);
    if (!src) return '';
    const url = new URL(src);
    if (url.hostname === 'cdn.shopify.com' || (url.origin === location.origin && url.pathname.includes('/cdn/shop/'))) url.searchParams.set('width', width);
    return url.href;
  }

  function clean(value) {
    if (!value || !/^\d+$/.test(String(value.id)) || typeof value.handle !== 'string' || !/^[\p{L}\p{N}_-]+$/u.test(value.handle)) return null;
    return {
      id: String(value.id), handle: value.handle,
      title: String(value.title || value.handle).slice(0, 1000),
      image: imageURL(value.image),
      savedAt: Number.isFinite(value.savedAt) ? value.savedAt : Date.now()
    };
  }

  function read() {
    try {
      const raw = JSON.parse(localStorage.getItem(key) || '[]');
      if (!Array.isArray(raw)) return new Map();
      return new Map(raw.slice(0, 500).map(clean).filter(Boolean).map(item => [item.id, item]));
    } catch (_) { return new Map(); }
  }

  function persist() {
    try { localStorage.setItem(key, JSON.stringify(Array.from(items.values()))); }
    catch (_) { persistent = false; }
    updateTools();
  }

  function announce(message) {
    if (dialog.open) { status.textContent = message; return; }
    clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toastTimer = setTimeout(() => { toast.hidden = true; }, 3200);
  }

  function sync(scope = document) {
    const toggles = Array.from(scope.querySelectorAll('[data-tm-favorite]'));
    if (scope instanceof Element && scope.matches('[data-tm-favorite]')) toggles.push(scope);
    toggles.forEach(button => {
      let product;
      try { product = clean(JSON.parse(button.dataset.tmFavoriteProduct)); } catch (_) { return; }
      if (!product) return;
      const saved = items.has(product.id);
      button.hidden = false;
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', (saved ? labels.remove : labels.add).replace('[product]', product.title));
      button.title = saved ? labels.saved : labels.save;
    });
    const openers = Array.from(scope.querySelectorAll('[data-tm-favorites-open]'));
    if (scope instanceof Element && scope.matches('[data-tm-favorites-open]')) openers.push(scope);
    openers.forEach(button => {
      button.hidden = false;
      button.classList.toggle('has-favorites', items.size > 0);
      button.setAttribute('aria-label', `${labels.favorites} (${items.size})`);
      const count = button.querySelector('[data-tm-favorites-count]');
      if (count) { count.textContent = items.size; count.hidden = items.size === 0; }
    });
    updateTools();
  }

  function node(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function price(data) {
    const currency = window.Shopify?.currency?.active || config.currency;
    try {
      const formatter = new Intl.NumberFormat(config.locale || document.documentElement.lang || 'en', { style: 'currency', currency });
      return `${data.price_varies ? labels.from + ' ' : ''}${formatter.format(data.price / 100)} ${currency}`;
    } catch (_) { return labels.check_price; }
  }

  function card(item) {
    const data = cache.get(item.id);
    const li = node('li', 'tm-favorites-item');
    li.dataset.tmFavoriteItem = item.id;
    const url = productURL(item);
    const photo = node('a', 'tm-favorites-image');
    photo.href = url;
    photo.tabIndex = -1;
    photo.setAttribute('aria-hidden', 'true');
    const src = imageURL(data?.featured_image || item.image);
    if (src) {
      const img = node('img');
      img.src = sizedImage(src, 600);
      img.alt = '';
      img.width = 400;
      img.height = 400;
      img.loading = 'lazy';
      img.decoding = 'async';
      photo.append(img);
      photo.title = labels.zoom_hint;
      photo.addEventListener('pointerenter', event => {
        if (hover.matches && event.pointerType !== 'touch') img.src = sizedImage(src, 1200);
      }, { once: true });
      photo.addEventListener('pointermove', event => {
        if (!hover.matches || event.pointerType === 'touch') return;
        const box = photo.getBoundingClientRect();
        photo.style.setProperty('--tm-zoom-x', `${Math.max(0, Math.min(100, (event.clientX - box.left) / box.width * 100))}%`);
        photo.style.setProperty('--tm-zoom-y', `${Math.max(0, Math.min(100, (event.clientY - box.top) / box.height * 100))}%`);
      });
      photo.addEventListener('pointerleave', () => {
        photo.style.removeProperty('--tm-zoom-x');
        photo.style.removeProperty('--tm-zoom-y');
      });
    } else { photo.append(node('span', 'tm-favorites-placeholder', '♡')); }
    li.append(photo);
    const heading = node('h3', 'tm-favorites-item-title');
    const title = node('a', '', data?.title || item.title);
    title.href = url;
    heading.append(title);
    li.append(heading);
    const row = node('div', 'tm-favorites-item-row');
    const priceText = !data ? labels.loading : data.missing ? labels.unavailable : data.failed ? labels.check_price : price(data);
    row.append(node('p', 'tm-favorites-price', priceText));
    const remove = node('button', 'tm-favorites-remove', shared ? items.has(item.id) ? labels.shared_saved : labels.save_item : labels.remove_item);
    remove.type = 'button';
    if (shared) { remove.dataset.tmSharedSave = item.id; remove.disabled = items.has(item.id); }
    else remove.dataset.tmFavoriteRemove = item.id;
    remove.setAttribute('aria-label', (shared ? labels.add : labels.remove).replace('[product]', data?.title || item.title));
    li.append(row);
    const actionHolder = node('div', 'tm-favorites-action');
    actionHolder.dataset.tmFavoriteAction = '';
    actionHolder.append(action(item));
    li.append(actionHolder);
    const error = node('p', 'tm-favorites-error', errors.get(item.id) || '');
    error.dataset.tmFavoriteError = '';
    error.hidden = !error.textContent;
    li.append(error);
    const links = node('div', 'tm-favorites-links');
    if (data?.missing) {
      photo.removeAttribute('href');
      title.removeAttribute('href');
    } else {
      const view = node('a', 'tm-favorites-details', labels.view_details);
      view.href = url;
      links.append(view);
    }
    links.append(remove);
    li.append(links);
    return li;
  }

  function render() {
    const active = document.activeElement;
    const focus = active?.closest('[data-tm-favorite-item]')?.dataset.tmFavoriteItem;
    const actionFocused = Boolean(active?.closest('[data-tm-favorite-action]'));
    const visible = Array.from(activeItems().values());
    visible.sort((a, b) => {
      if (sort === 'recent') return b.savedAt - a.savedAt;
      const pa = cache.get(a.id)?.price;
      const pb = cache.get(b.id)?.price;
      if (!Number.isFinite(pa)) return Number.isFinite(pb) ? 1 : b.savedAt - a.savedAt;
      if (!Number.isFinite(pb)) return -1;
      return sort === 'price_asc' ? pa - pb : pb - pa;
    });
    const fragment = document.createDocumentFragment();
    visible.forEach(item => fragment.append(card(item)));
    grid.replaceChildren(fragment);
    const empty = dialog.querySelector('[data-tm-favorites-empty]');
    empty.hidden = visible.length > 0;
    updateTools();
    if (focus && dialog.open) {
      const selector = actionFocused ? `[data-tm-favorite-item="${focus}"] [data-tm-favorite-action] > *` : `[data-tm-favorite-item="${focus}"] .tm-favorites-remove:not(:disabled)`;
      const target = grid.querySelector(selector) || grid.querySelector('[data-tm-favorite-remove]') || dialog.querySelector('[data-tm-favorites-close]');
      target.focus({ preventScroll: true });
    }
  }

  async function refresh() {
    const currentRun = ++run;
    const queue = Array.from(activeItems().values());
    if (!queue.length) { status.textContent = ''; grid.removeAttribute('aria-busy'); return; }
    grid.setAttribute('aria-busy', 'true');
    status.textContent = labels.loading;
    let failed = false;
    async function worker() {
      while (queue.length && currentRun === run) {
        const item = queue.shift();
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        let data;
        try {
          const response = await fetch(`${root}products/${encodeURIComponent(item.handle)}.js`, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
          if (response.status === 404 || response.status === 410) data = { missing: true };
          else {
            if (!response.ok) throw new Error('Product unavailable');
            data = await response.json();
            if (String(data.id) !== item.id || !Number.isFinite(data.price)) throw new Error('Invalid product');
          }
        } catch (_) { data = { failed: true }; failed = true; }
        finally { clearTimeout(timeout); }
        if (currentRun !== run) return;
        if (activeItems().has(item.id)) cache.set(item.id, data);
      }
    }
    await Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker));
    if (currentRun !== run) return;
    grid.removeAttribute('aria-busy');
    status.textContent = failed ? labels.partial : '';
    render();
  }

  function changed() {
    persist();
    sync();
    if (dialog.open) render();
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const toggle = event.target.closest('[data-tm-favorite]');
    const open = event.target.closest('[data-tm-favorites-open]');
    const remove = event.target.closest('[data-tm-favorite-remove]');
    const close = event.target.closest('[data-tm-favorites-close]');
    const add = event.target.closest('[data-tm-favorite-cart-add]');
    if (!toggle && !open && !remove && !close && !add) return;
    event.preventDefault();
    event.stopPropagation();
    if (toggle) {
      let product;
      try { product = clean(JSON.parse(toggle.dataset.tmFavoriteProduct)); } catch (_) { return; }
      if (!product) return;
      if (persistent) items = read();
      const saved = items.has(product.id);
      if (saved) items.delete(product.id);
      else {
        if (items.size >= 500) { announce(labels.limit); return; }
        items.set(product.id, product);
      }
      changed();
      announce(persistent ? (saved ? labels.removed : labels.added) : labels.temporary_note);
    } else if (open) {
      openList(open);
    } else if (remove) {
      removeItems([remove.dataset.tmFavoriteRemove], labels.removed);
    } else if (close) dialog.close();
    else if (add && !add.disabled) addToCart(add.dataset.tmFavoriteCartAdd);
  }, true);

  dialog.querySelector('[data-tm-favorites-share]').addEventListener('click', event => showShare(event.currentTarget));
  dialog.querySelector('[data-tm-favorites-clear]').addEventListener('click', event => {
    if (persistent) items = read();
    clearSelection = Array.from(items.keys());
    if (clearSelection.length) setPanel('clear', event.currentTarget);
  });
  dialog.querySelector('[data-tm-favorites-clear-confirm]').addEventListener('click', () => removeItems(clearSelection, labels.cleared));
  dialog.querySelectorAll('[data-tm-panel-close]').forEach(button => button.addEventListener('click', () => { setPanel(''); panelTrigger?.focus({ preventScroll: true }); }));
  dialog.querySelector('[data-tm-favorites-undo]').addEventListener('click', restoreItems);
  dialog.querySelector('[data-tm-favorites-copy]').addEventListener('click', copyShareLink);
  dialog.querySelector('[data-tm-favorites-native]').addEventListener('click', async () => {
    try { await navigator.share({ title: document.title, url: dialog.querySelector('[data-tm-favorites-share-link]').value }); }
    catch (error) { if (error.name !== 'AbortError') announce(labels.share_failed); }
  });
  dialog.querySelector('[data-tm-favorites-login]').addEventListener('click', () => {
    try { sessionStorage.setItem(resumeKey, String(Date.now())); } catch (_) {}
  });
  dialog.querySelector('[data-tm-favorites-save-all]').addEventListener('click', () => saveShared(Array.from(shared?.keys() || [])));
  dialog.querySelector('[data-tm-favorites-own]').addEventListener('click', () => openList(null));
  grid.addEventListener('click', event => {
    const button = event.target.closest('[data-tm-shared-save]');
    if (button && !button.disabled) saveShared([button.dataset.tmSharedSave]);
  });
  dialog.addEventListener('cancel', event => {
    if (dialog.querySelector('[data-tm-panel]:not([hidden])')) { event.preventDefault(); setPanel(''); panelTrigger?.focus({ preventScroll: true }); }
  });

  dialog.querySelector('[data-tm-favorites-sort]').addEventListener('change', event => { sort = event.target.value; render(); });
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => {
    run += 1;
    document.documentElement.classList.remove('tm-favorites-open');
    grid.removeAttribute('aria-busy');
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  });
  window.addEventListener('storage', event => {
    if (event.key === cartKey) { readCart(); return; }
    if (event.key !== key && event.key !== null) return;
    items = read();
    sync();
    if (dialog.open) { render(); refresh(); }
  });
  window.addEventListener('pageshow', event => { if (persistent) items = read(); sync(); if (event.persisted) readCart(); });
  window.addEventListener('focus', () => { if (dialog.open && !adding) readCart(); });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => { if (event.source !== 'tm-favorites') return readCart(); });
  }
  const observer = new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(added => { if (added instanceof Element && !dialog.contains(added)) sync(added); }));
  });
  items = read();
  sync();
  observer.observe(document.body, { childList: true, subtree: true });
  window.TMFavorites = { sync };
  window.addEventListener('hashchange', openSharedLink);
  if (!openSharedLink()) {
    try {
      const pending = Number(sessionStorage.getItem(resumeKey));
      if (pending && (config.logged_in || Date.now() - pending > 600000)) {
        sessionStorage.removeItem(resumeKey);
        if (config.logged_in && Date.now() - pending <= 600000 && items.size) {
          openList(null);
          showShare(dialog.querySelector('[data-tm-favorites-share]'));
        }
      }
    } catch (_) {}
  }
})();
